import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle, ArrowRight, CalendarRange, FileText, IndianRupee, Loader2,
  Plus, TrendingUp, WalletCards,
} from "lucide-react";
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from "recharts";
import { api, fmtDate, inr, inr0 } from "@/lib/api";
import { can, useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";

const TEAL = "#0D4F4A";
const GOLD = "#C9A227";
const SKY = "#0284C7";

function Kpi({ label, value, sub, testid, accent = false, to }) {
  const inner = (
    <div className={`bg-white border border-[#E5E0D8] rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow border-l-4 ${accent ? "border-l-[#C9A227]" : "border-l-[#0D4F4A]"}`}>
      <div className="text-xs font-semibold uppercase tracking-wide text-[#5C6B69]">{label}</div>
      <div className="num text-xl sm:text-2xl font-bold text-[#0D4F4A] mt-1" data-testid={testid}>{value}</div>
      {sub && <div className="text-xs text-[#5C6B69] mt-0.5">{sub}</div>}
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

function Section({ title, children, action }) {
  return (
    <div className="bg-white border border-[#E5E0D8] rounded-xl shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-base font-semibold text-[#1A2E2C]">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return <div className="flex items-center justify-center py-32 text-[#5C6B69]">
      <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading dashboard…</div>;
  }

  const { today, month, fy, charts, gst_health, upcoming_trips, overdue_invoices, recent_invoices } = data;
  const healthTotal = (gst_health.b2b_missing_gstin || 0) + (gst_health.missing_pos || 0) + (gst_health.draft_invoices || 0);
  const quick = [
    { label: "New Invoice", to: "/invoices/new", module: "invoices", id: "dash-new-invoice" },
    { label: "New Booking", to: "/bookings?new=1", module: "bookings", id: "dash-new-booking" },
    { label: "New Quotation", to: "/quotations?new=1", module: "quotations", id: "dash-new-quotation" },
    { label: "Add Payment", to: "/payments?new=1", module: "payments", id: "dash-add-payment" },
    { label: "Add Expense", to: "/expenses?new=1", module: "expenses", id: "dash-add-expense" },
  ].filter((a) => can(user?.role, a.module, true));

  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Financial Dashboard" subtitle={`How Dreamzz Travels is doing · FY ${fy.fy}`}
        testid="dashboard-header"
        actions={quick.map((a) => (
          <Button key={a.to} size="sm" variant={a.label === "New Invoice" ? "default" : "outline"}
            className={a.label === "New Invoice" ? "bg-[#0D4F4A] hover:bg-[#136A63]" : "border-[#E5E0D8] bg-white"}
            onClick={() => navigate(a.to)} data-testid={a.id}>
            <Plus className="h-3.5 w-3.5 mr-1" />{a.label}
          </Button>
        ))} />

      <div className="bg-gradient-to-r from-[#0D4F4A]/10 via-[#C9A227]/15 to-[#FAF8F5] border border-[#C9A227]/40 rounded-xl p-4 mb-6 flex items-center justify-between flex-wrap gap-3"
        data-testid="gst-health-banner">
        <div className="flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-[#C9A227]" />
          <div>
            <div className="text-sm font-semibold text-[#1A2E2C]">GST Health</div>
            <div className="text-xs text-[#5C6B69]">
              {healthTotal === 0 ? "No blocking issues found. Data looks filing-ready." :
                `${healthTotal} item(s) need attention before GST filing`}
              {" · "}Missing GSTIN: <b>{gst_health.b2b_missing_gstin + gst_health.customers_missing_gstin}</b>
              {" · "}Missing place of supply: <b>{gst_health.missing_pos}</b>
              {" · "}Draft invoices: <b>{gst_health.draft_invoices}</b>
            </div>
          </div>
        </div>
        <Button size="sm" variant="outline" className="border-[#C9A227] text-[#8a6d14] bg-white/70"
          onClick={() => navigate("/gst?tab=issues")} data-testid="gst-health-review-button">
          Review Issues <ArrowRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      </div>

      <div className="space-y-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#5C6B69] mb-2">Today</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Kpi label="Sales" value={inr0(today.sales)} testid="kpi-todays-sales" />
            <Kpi label="Collections" value={inr0(today.collections)} testid="kpi-todays-collections" />
            <Kpi label="Outstanding" value={inr0(today.outstanding)} testid="kpi-todays-outstanding" to="/receivables" />
            <Kpi label="Expenses" value={inr0(today.expenses)} testid="kpi-todays-expenses" />
            <Kpi label="Net Position" value={inr0(today.net)} testid="kpi-todays-net" accent />
          </div>
        </div>

        <div>
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#5C6B69] mb-2">This Month</div>
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
            <Kpi label="Total Sales" value={inr0(month.sales)} testid="kpi-month-sales" />
            <Kpi label="Taxable Sales" value={inr0(month.taxable)} testid="kpi-month-taxable" />
            <Kpi label="GST Collected" value={inr0(month.gst_collected)} testid="kpi-month-gst" />
            <Kpi label="Expenses" value={inr0(month.expenses)} testid="kpi-month-expenses" />
            <Kpi label="Input Credit" value={inr0(month.itc)} testid="kpi-month-itc" />
            <Kpi label="Net GST" value={inr0(month.net_gst)} testid="kpi-month-net-gst" accent />
            <Kpi label="Invoices" value={month.invoices} testid="kpi-month-invoices" />
            <Kpi label="Bookings" value={month.bookings} testid="kpi-month-bookings" />
          </div>
        </div>

        <div>
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#5C6B69] mb-2">Financial Year {fy.fy}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
            <Kpi label="Gross Revenue" value={inr0(fy.revenue)} testid="kpi-fy-revenue" />
            <Kpi label="Taxable Revenue" value={inr0(fy.taxable)} testid="kpi-fy-taxable" />
            <Kpi label="GST Collected" value={inr0(fy.gst)} testid="kpi-fy-gst" />
            <Kpi label="Expenses" value={inr0(fy.expenses)} testid="kpi-fy-expenses" />
            <Kpi label="Input GST" value={inr0(fy.itc)} testid="kpi-fy-itc" />
            <Kpi label="Est. GST Liability" value={inr0(fy.net_gst)} testid="kpi-fy-net-gst" accent />
            <Kpi label="Receivables" value={inr0(fy.receivables)} testid="kpi-fy-receivables" />
            <Kpi label="Payables" value={inr0(fy.payables)} testid="kpi-fy-payables" />
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div className="xl:col-span-2">
            <Section title="Monthly Sales & GST Collected">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={charts.monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#5C6B69" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#5C6B69" }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => inr(v)} />
                  <Legend />
                  <Bar dataKey="sales" name="Sales" fill={TEAL} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="gst" name="GST" fill={GOLD} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Section>
          </div>
          <Section title="B2B vs B2C (FY)">
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={charts.b2b_b2c} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  <Cell fill={TEAL} /><Cell fill={GOLD} />
                </Pie>
                <Tooltip formatter={(v) => inr(v)} />
              </PieChart>
            </ResponsiveContainer>
          </Section>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <Section title="Expense Categories (Month)">
            {charts.expense_categories.length === 0 ? <p className="text-sm text-[#5C6B69]">No expenses this month.</p> : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={charts.expense_categories} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="category" width={90} tick={{ fontSize: 11, fill: "#5C6B69" }} />
                  <Tooltip formatter={(v) => inr(v)} />
                  <Bar dataKey="amount" fill={SKY} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Section>
          <Section title="Collections vs Outstanding (Month)">
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={charts.collections_vs_outstanding} dataKey="value" nameKey="name"
                  innerRadius={50} outerRadius={75} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  <Cell fill="#16A34A" /><Cell fill="#DC2626" />
                </Pie>
                <Tooltip formatter={(v) => inr(v)} />
              </PieChart>
            </ResponsiveContainer>
          </Section>
          <Section title="Revenue by Package (FY)">
            {charts.revenue_by_package.length === 0 ? <p className="text-sm text-[#5C6B69]">No package revenue yet.</p> : (
              <ul className="space-y-2.5">
                {charts.revenue_by_package.map((p, i) => (
                  <li key={i} className="flex items-center justify-between text-sm">
                    <span className="truncate pr-3">{p.package}</span>
                    <span className="num font-semibold text-[#0D4F4A] shrink-0">{inr0(p.amount)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <Section title="Upcoming Trips (15 days)" action={<Link to="/bookings" className="text-xs text-[#0D4F4A] font-semibold hover:underline">View all</Link>}>
            {upcoming_trips.length === 0 ? <p className="text-sm text-[#5C6B69]">No upcoming departures.</p> : (
              <ul className="space-y-2">
                {upcoming_trips.map((b) => (
                  <li key={b.id} className="flex items-center gap-3 text-sm border-b border-[#E5E0D8]/60 pb-2 last:border-0">
                    <CalendarRange className="h-4 w-4 text-[#C9A227] shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{b.customer_name} → {b.destination}</div>
                      <div className="text-xs text-[#5C6B69]">{b.booking_id} · {fmtDate(b.start_date)} · {b.travellers} pax</div>
                    </div>
                    <StatusBadge value={b.status} />
                  </li>
                ))}
              </ul>
            )}
          </Section>
          <Section title="Overdue Payments" action={<Link to="/receivables" className="text-xs text-[#0D4F4A] font-semibold hover:underline">Receivables</Link>}>
            {overdue_invoices.length === 0 ? <p className="text-sm text-[#5C6B69]">No overdue invoices. Great!</p> : (
              <ul className="space-y-2">
                {overdue_invoices.map((i) => (
                  <li key={i.id} className="flex items-center gap-3 text-sm border-b border-[#E5E0D8]/60 pb-2 last:border-0">
                    <IndianRupee className="h-4 w-4 text-red-500 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{i.invoice_no} · {i.customer?.name}</div>
                      <div className="text-xs text-[#5C6B69]">Due {fmtDate(i.due_date)}</div>
                    </div>
                    <span className="num text-sm font-semibold text-red-600">{inr0(i.balance)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Section>
          <Section title="Recent Invoices" action={<Link to="/invoices" className="text-xs text-[#0D4F4A] font-semibold hover:underline">All invoices</Link>}>
            <ul className="space-y-2">
              {recent_invoices.map((i) => (
                <li key={i.id} className="flex items-center gap-3 text-sm border-b border-[#E5E0D8]/60 pb-2 last:border-0 cursor-pointer hover:bg-[#FAF8F5] -mx-2 px-2 rounded"
                  onClick={() => navigate(`/invoices/${i.id}`)} data-testid={`recent-invoice-${i.id}`}>
                  <FileText className="h-4 w-4 text-[#0D4F4A] shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{i.invoice_no || "DRAFT"} · {i.customer?.name}</div>
                    <div className="text-xs text-[#5C6B69]">{fmtDate(i.date)}</div>
                  </div>
                  <span className="num text-sm font-semibold text-[#0D4F4A]">{inr0(i.grand_total)}</span>
                </li>
              ))}
            </ul>
          </Section>
        </div>
      </div>
    </div>
  );
}
